/*
	Countdown controller — data-attribute driven timers.

	Any element matching `[data-countdown]` is initialized once on DOMContentLoaded.
	Manual init / re-init is available via window.Elixir_InitCountdowns(root).

	Root element attributes:
		data-countdown                  Presence only — marks a countdown root.
		data-countdown-deadline         Absolute end time (ISO-8601 string, or ms epoch).
		data-countdown-duration-ms      Duration from first-view in milliseconds. Mutually exclusive with -deadline.
		data-countdown-persist-key      If set with -duration-ms, end time is stored under this localStorage key
		                                so the same expiry persists across reloads.
		data-countdown-midnight-tz      IANA timezone string (e.g. "America/New_York"). When set (and no
		                                -deadline/-duration-ms is present), the timer counts down to tonight's
		                                midnight in that timezone. Resets automatically on next page load.
		data-countdown-format           "units" (default) | "hms" | "ms"
		                                  units: writes per-unit elements (see data-unit)
		                                  hms:   writes full HH:MM:SS string into element marked [data-countdown-text]
		                                  ms:    writes M:SS string into [data-countdown-text] (e.g. cart timer bar)
		data-countdown-show-days        "true" (default if no format attr implies otherwise) — include days unit.
		                                Default: true. Explicit "false" hides days even in units mode.
		data-countdown-on-complete      "zeros" (default) | "hide" | "remove" | "class"
		                                "zeros": keep element visible, fill all units with 00.
		                                "hide":  set style.display = "none".
		                                "remove": remove from DOM.
		                                "class": add class named by -complete-class.
		data-countdown-complete-class   Class name to add when -on-complete="class".
		data-countdown-complete-event   If set, dispatches a bubbling custom event by this name from the root on completion.

	Per-unit child elements:
		<span data-unit="days">00</span>
		<span data-unit="hours">00</span>
		<span data-unit="minutes">00</span>
		<span data-unit="seconds">00</span>
	(Any missing unit element is skipped; days element is auto-hidden when days <= 0 unless `-show-days` is forced true.)

	Global hooks:
		window.Elixir_Countdown.init(root)          Initialize one element (no-op if already initialized).
		window.Elixir_Countdown.initAll(scope?)     Initialize all [data-countdown] in scope (default: document).
		window.Elixir_Countdown.stop(root)          Cancel a running timer for an element.
*/
(function () {
	"use strict";

	var INSTANCES = new WeakMap();
	var RAF_STATE = { running: false, subs: new Set() };

	function pad(n) {
		return String(n).padStart(2, "0");
	}

	function parseDeadline(raw) {
		if (!raw) return null;
		var asNum = Number(raw);
		if (!Number.isNaN(asNum) && asNum > 0) return asNum;
		var parsed = Date.parse(raw);
		return Number.isNaN(parsed) ? null : parsed;
	}

	function getTonightMidnight(tz) {
		var now = new Date();
		try {
			var formatter = new Intl.DateTimeFormat("en-US", {
				timeZone: tz,
				year: "numeric",
				month: "2-digit",
				day: "2-digit",
				hour: "2-digit",
				minute: "2-digit",
				second: "2-digit",
				hour12: false,
			});
			var parts = formatter.formatToParts(now).reduce(function (acc, p) {
				if (p.type !== "literal") acc[p.type] = Number(p.value);
				return acc;
			}, {});
			// Compute offset between zone's wall-clock "now" and real UTC now,
			// then use that to find the real UTC ms that corresponds to zone midnight.
			var zoneNowMs = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);
			var zoneMidnightMs = Date.UTC(parts.year, parts.month - 1, parts.day + 1, 0, 0, 0);
			return Date.now() + (zoneMidnightMs - zoneNowMs);
		} catch (e) {
			// Fallback: next UTC midnight
			var d = new Date();
			d.setUTCHours(24, 0, 0, 0);
			return d.getTime();
		}
	}

	function resolveEndTime(root) {
		var ds = root.dataset;
		var deadline = parseDeadline(ds.countdownDeadline);
		if (deadline) return deadline;

		var duration = parseInt(ds.countdownDurationMs, 10);
		if (Number.isFinite(duration) && duration > 0) {
			var key = ds.countdownPersistKey;
			if (key) {
				try {
					var stored = parseInt(localStorage.getItem(key), 10);
					if (Number.isFinite(stored) && stored > Date.now()) return stored;
					var end = Date.now() + duration;
					localStorage.setItem(key, String(end));
					return end;
				} catch (e) {
					/* localStorage unavailable — fall through to non-persisted */
				}
			}
			return Date.now() + duration;
		}

		if (ds.countdownMidnightTz) {
			return getTonightMidnight(ds.countdownMidnightTz);
		}

		return null;
	}

	function breakdown(remainingMs) {
		var total = Math.max(0, Math.floor(remainingMs / 1000));
		return {
			days: Math.floor(total / 86400),
			hours: Math.floor((total % 86400) / 3600),
			minutes: Math.floor((total % 3600) / 60),
			seconds: total % 60,
		};
	}

	function writeUnits(inst, parts) {
		var showDays = inst.showDays;
		if (inst.els.days) {
			inst.els.days.textContent = pad(parts.days);
			if (!showDays && parts.days <= 0 && !inst._daysHidden) {
				inst.els.days.style.display = "none";
				var wrap = inst.els.days.closest("[data-unit-wrap='days']");
				if (wrap) wrap.style.display = "none";
				inst._daysHidden = true;
			}
		}
		if (inst.els.hours) inst.els.hours.textContent = pad(parts.hours + (showDays ? 0 : parts.days * 24));
		if (inst.els.minutes) inst.els.minutes.textContent = pad(parts.minutes);
		if (inst.els.seconds) inst.els.seconds.textContent = pad(parts.seconds);
	}

	function writeHms(inst, parts) {
		if (!inst.els.text) return;
		var h = parts.hours + parts.days * 24;
		inst.els.text.textContent = pad(h) + ":" + pad(parts.minutes) + ":" + pad(parts.seconds);
	}

	function writeMs(inst, parts) {
		if (!inst.els.text) return;
		var m = parts.minutes + (parts.hours + parts.days * 24) * 60;
		inst.els.text.textContent = m + ":" + pad(parts.seconds);
	}

	function tick(inst) {
		if (inst.stopped) return;
		var remaining = inst.endTime - Date.now();

		if (remaining <= 0) {
			var zeros = { days: 0, hours: 0, minutes: 0, seconds: 0 };
			inst.writer(inst, zeros);
			complete(inst);
			return;
		}

		var parts = breakdown(remaining);
		inst.writer(inst, parts);
	}

	function complete(inst) {
		inst.stopped = true;
		unsubscribe(inst);
		var ds = inst.root.dataset;
		var mode = ds.countdownOnComplete || "zeros";
		if (mode === "hide") {
			inst.root.style.display = "none";
		} else if (mode === "remove") {
			if (inst.root.parentNode) inst.root.parentNode.removeChild(inst.root);
		} else if (mode === "class") {
			var cls = ds.countdownCompleteClass;
			if (cls) inst.root.classList.add(cls);
		}
		if (ds.countdownCompleteEvent) {
			inst.root.dispatchEvent(new CustomEvent(ds.countdownCompleteEvent, { bubbles: true }));
		}
	}

	function subscribe(inst) {
		RAF_STATE.subs.add(inst);
		if (!RAF_STATE.running) {
			RAF_STATE.running = true;
			RAF_STATE.lastTick = 0;
			requestAnimationFrame(rafLoop);
		}
	}

	function unsubscribe(inst) {
		RAF_STATE.subs.delete(inst);
		if (RAF_STATE.subs.size === 0) RAF_STATE.running = false;
	}

	function rafLoop(ts) {
		if (!RAF_STATE.running) return;
		if (ts - RAF_STATE.lastTick >= 1000) {
			RAF_STATE.lastTick = ts;
			RAF_STATE.subs.forEach(tick);
		}
		requestAnimationFrame(rafLoop);
	}

	function pickWriter(format) {
		if (format === "hms") return writeHms;
		if (format === "ms") return writeMs;
		return writeUnits;
	}

	function init(root) {
		if (!root || INSTANCES.has(root)) return INSTANCES.get(root);
		var end = resolveEndTime(root);
		if (!end) return null;

		var ds = root.dataset;
		var format = ds.countdownFormat || "units";
		var showDays = ds.countdownShowDays !== "false";

		var inst = {
			root: root,
			endTime: end,
			showDays: showDays,
			writer: pickWriter(format),
			stopped: false,
			els: {
				days: root.querySelector("[data-unit='days']"),
				hours: root.querySelector("[data-unit='hours']"),
				minutes: root.querySelector("[data-unit='minutes']"),
				seconds: root.querySelector("[data-unit='seconds']"),
				text: root.querySelector("[data-countdown-text]"),
			},
		};

		INSTANCES.set(root, inst);
		tick(inst);
		if (!inst.stopped) subscribe(inst);

		return inst;
	}

	function stop(root) {
		var inst = INSTANCES.get(root);
		if (!inst) return;
		inst.stopped = true;
		unsubscribe(inst);
		INSTANCES.delete(root);
	}

	function initAll(scope) {
		scope = scope || document;
		scope.querySelectorAll("[data-countdown]").forEach(init);
	}

	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", function () {
			initAll();
		});
	} else {
		initAll();
	}

	document.addEventListener("shopify:section:unload", function (e) {
		if (!e.target) return;
		e.target.querySelectorAll("[data-countdown]").forEach(stop);
	});

	document.addEventListener("shopify:section:load", function (e) {
		if (e.target) initAll(e.target);
	});

	document.addEventListener("shopify:block:select", function (e) {
		if (e.target) initAll(e.target);
	});

	if (typeof window !== "undefined") {
		window.Elixir_Countdown = { init: init, initAll: initAll, stop: stop };
		window.Elixir_InitCountdowns = initAll;
	}
})();
